import React from 'react';

import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { useReportingLines } from '@atlassian/teams';

import { ReportingLinesEmptyState } from './ReportingLines/EmptyState';
import { ReportingLinesErrorState } from './ReportingLines/ErrorState';
import { ReportingLinesChartFromResponse } from './ReportingLines/ReportingLinesChart';
import { responseHasReportingLinesData } from './utils';

export type ReportingLinesProps = {
	reportingLinesResponse: ReturnType<typeof useReportingLines>;
};

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		paddingTop: token('space.100', '8px'),
		paddingRight: token('space.100', '8px'),
		paddingBottom: token('space.100', '8px'),
		paddingLeft: token('space.100', '8px'),
		borderRadius: token('radius.small'),
	},
});

const ChartContainer = Box;

export const ReportingLines = (props: ReportingLinesProps): React.JSX.Element => {
	const { reportingLinesResponse } = props;

	const hasReportingLinesData = responseHasReportingLinesData(reportingLinesResponse);
	const { isLoading, error } = reportingLinesResponse;
	const isUserProfileRefreshExperimentEnabled =
		FeatureGates.initializeCalled() &&
		FeatureGates.getExperimentValue('user_profile_refresh_experiment', 'isEnabled', false);

	if (error && !isLoading && isUserProfileRefreshExperimentEnabled) {
		return <ReportingLinesErrorState />;
	}

	if (!hasReportingLinesData && !(isLoading && isUserProfileRefreshExperimentEnabled)) {
		return <ReportingLinesEmptyState />;
	}
	return (
		<ChartContainer xcss={styles.container}>
			<ReportingLinesChartFromResponse
				reportingLinesResponse={reportingLinesResponse}
				isResponseEmpty={!hasReportingLinesData}
			/>
		</ChartContainer>
	);
};

export default ReportingLines;
